import { useRef, useState, type ReactNode } from 'react';
import type { DomainCreated } from '@traceio-org/platform-client';
import { normalizeHostname, type AdminDomain, type ProductKey } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageDialog, PageSection } from '@traceio-org/ui';
import type { DomainAction } from '../../api';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { RadioGroup, ReasonField, SelectField, TextField, reasonError } from '../../components/fields';
import { EMPLOYEE_PRODUCTS } from '../../components/grants';
import { DateText, ScrollTable, StatusBadge } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { DOMAIN_KIND_LABELS, productName } from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { useOrganizationScope, useSupportAccessHint } from './scope';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const KIND_CHOICES = [
  {
    value: 'employee-app',
    label: 'Employee app',
    description: "Where the organization's people sign in and work, in one product.",
  },
  {
    value: 'customer-portal',
    label: 'Customer portal',
    description: "Where the organization's own customers sign in to its portal.",
  },
] as const;
type DomainKind = (typeof KIND_CHOICES)[number]['value'];

const HOSTNAME_PROBLEMS: Readonly<Record<string, string>> = {
  empty: 'Enter a host name.',
  too_long: 'A host name has at most 253 characters.',
  invalid_characters: 'Enter a host name only, without https://, a path or a port.',
  ip_literal: 'Enter a host name, not an IP address.',
  invalid_label: 'Each part of the host name has letters, digits and hyphens, and no hyphen at either end.',
  single_label: 'Enter a full host name, for example app.example.com.',
};

/** The actions a domain in this state can take; platform-api checks again. */
export function domainActions(domain: AdminDomain): DomainAction[] {
  if (domain.lifecycle === 'released' || domain.lifecycle === 'deprovisioning') return [];
  const actions: DomainAction[] = [];
  if (domain.verificationStatus !== 'verified') actions.push('verify');
  if (domain.lifecycle === 'active') actions.push('disable');
  if (domain.lifecycle === 'disabled') actions.push('enable');
  actions.push('release');
  return actions;
}

const ACTION_LABELS: Readonly<Record<DomainAction, string>> = {
  verify: 'Verify',
  disable: 'Disable',
  enable: 'Enable',
  release: 'Release',
};

interface AddForm {
  hostname: string;
  kind: DomainKind;
  applicationKey: string;
  portalId: string;
}

type AddErrors = Partial<Record<keyof AddForm, string>>;

function validateAdd(form: AddForm): { errors: AddErrors; hostname: string | null } {
  const errors: AddErrors = {};
  const result = normalizeHostname(form.hostname);
  if (!result.ok) errors.hostname = HOSTNAME_PROBLEMS[result.reason] ?? 'Enter a valid host name.';
  if (!form.applicationKey) errors.applicationKey = 'Choose the product the host serves.';
  if (form.kind === 'customer-portal' && form.portalId.trim() && !UUID.test(form.portalId.trim())) {
    errors.portalId = 'A portal ID looks like 0190a000-0000-7000-8000-000000000751.';
  }
  return { errors, hostname: result.ok ? result.hostname : null };
}

function DomainNote({ domain }: { domain: AdminDomain }) {
  if (domain.lifecycle === 'released') {
    return (
      <>
        Released
        {domain.releasedAt ? (
          <>
            {' '}
            <DateText iso={domain.releasedAt} />
          </>
        ) : null}
        .
        {domain.reassignableAfter ? (
          <>
            {' '}
            Held until <DateText iso={domain.reassignableAfter} />.
          </>
        ) : null}
      </>
    );
  }
  if (domain.verifiedAt) {
    return (
      <>
        Verified <DateText iso={domain.verifiedAt} />.
      </>
    );
  }
  return <span className="admin-muted">Not verified yet.</span>;
}

/** The DNS record the customer publishes, shown once after the host is added. */
function VerificationRecord({ created, onDone }: { created: DomainCreated | null; onDone: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(`${label} copied.`);
    } catch {
      setCopied(`Copying is not available here: select the ${label.toLowerCase()} and copy it yourself.`);
    }
  };
  return (
    <PageDialog
      open={created !== null}
      onOpenChange={(open) => {
        if (!open) onDone();
      }}
      title="Publish this DNS record"
      description="Trace shows the value only now. Copy it before you close this."
      footer={
        <div className="admin-dialog-actions">
          <button type="button" className="admin-action admin-action--primary" onClick={onDone}>
            Done
          </button>
        </div>
      }
    >
      {created ? (
        <div className="admin-dialog-form">
          <p>
            The customer publishes this record in the DNS of <code>{created.domain.hostname}</code> to prove
            they own it. Once it is published (DNS changes can take a while), use Verify on the host.
          </p>
          <dl className="admin-record" aria-label="DNS record">
            <div>
              <dt>Type</dt>
              <dd>
                <code>{created.recordType}</code>
              </dd>
            </div>
            <div>
              <dt>Name</dt>
              <dd>
                <code>{created.recordName}</code>
                <button
                  type="button"
                  className="admin-action"
                  onClick={() => void copy('Name', created.recordName)}
                >
                  Copy the name
                </button>
              </dd>
            </div>
            <div>
              <dt>Value</dt>
              <dd>
                <code>{created.recordValue}</code>
                <button
                  type="button"
                  className="admin-action"
                  onClick={() => void copy('Value', created.recordValue)}
                >
                  Copy the value
                </button>
              </dd>
            </div>
          </dl>
          <p className="admin-notice" role="status" aria-live="polite">
            {copied}
          </p>
        </div>
      ) : null}
    </PageDialog>
  );
}

type DomainDialog = { kind: 'add' } | { kind: 'action'; action: DomainAction; domain: AdminDomain };

/**
 * Domains (/organizations/:id/domains): the organization's custom hosts with their verification, TLS and
 * lifecycle, released ones included. Staff who may manage domains add a host (the TXT record to publish is
 * shown once), verify it, disable and enable it, and release it; every action takes a reason, and the
 * release explains the cooldown before another organization can claim the host.
 */
export function DomainsTab() {
  const { organization, abilities } = useOrganizationScope();
  const hint = useSupportAccessHint();
  const { api } = useAdmin();
  const organizationId = organization.id;
  const key = `domains\u0000${organizationId}`;
  const read = useAdminRead(key, (signal) => api.listDomains(organizationId, signal));
  const domains = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;

  const [notice, setNotice] = useState<string | null>(null);
  const [created, setCreated] = useState<DomainCreated | null>(null);
  const lastCreated = useRef<DomainCreated | null>(null);
  const dialog = useDialogTarget<DomainDialog>();
  const [form, setForm] = useState<AddForm>({
    hostname: '',
    kind: 'employee-app',
    applicationKey: '',
    portalId: '',
  });
  const [errors, setErrors] = useState<AddErrors>({});
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  const name = organization.displayName;

  const open = (next: DomainDialog) => {
    setErrors({});
    setReason('');
    setReasonProblem(null);
    if (next.kind === 'add')
      setForm({ hostname: '', kind: 'employee-app', applicationKey: '', portalId: '' });
    dialog.show(next);
  };

  const productOptions = (kind: DomainKind) => [
    { value: '', label: 'Choose a product' },
    ...(kind === 'customer-portal' ? (['portal'] as ProductKey[]) : EMPLOYEE_PRODUCTS).map((product) => ({
      value: product,
      label: productName(product),
    })),
  ];

  const target = dialog.target;
  let dialogView: ReactNode = null;
  if (target?.kind === 'add') {
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={`Add a custom domain for ${name}`}
        description="It serves nobody until it is verified through DNS."
        submitLabel="Add the domain"
        onSubmit={async () => {
          const { errors: found, hostname } = validateAdd(form);
          setErrors(found);
          if (Object.keys(found).length > 0 || !hostname) return 'invalid';
          const portalId = form.portalId.trim();
          lastCreated.current = null;
          lastCreated.current = await api.createDomain(organizationId, {
            hostname,
            kind: form.kind,
            applicationKey: form.applicationKey as ProductKey,
            ...(form.kind === 'customer-portal' && portalId ? { portalId } : {}),
          });
          return undefined;
        }}
        onSaved={() => {
          const result = lastCreated.current;
          setNotice(
            result
              ? `${result.domain.hostname} is added. Verify it once the DNS record is published.`
              : "The domain is added, but Trace's answer did not include its DNS record. Release it and add it again for a new record.",
          );
          setCreated(result);
          read.retry();
        }}
      >
        <TextField
          label="Host name"
          value={form.hostname}
          onChange={(value) => setForm((current) => ({ ...current, hostname: value }))}
          hint="For example app.northwind.com, without https:// or a path."
          error={errors.hostname ?? null}
          maxLength={253}
          autoComplete="off"
          spellCheck={false}
          required
        />
        <RadioGroup
          legend="Kind"
          choices={KIND_CHOICES}
          value={form.kind}
          onChange={(value) =>
            setForm((current) => ({
              ...current,
              kind: value as DomainKind,
              applicationKey: value === 'customer-portal' ? 'portal' : '',
            }))
          }
        />
        <SelectField
          label="Product"
          value={form.applicationKey}
          options={productOptions(form.kind)}
          onChange={(value) => setForm((current) => ({ ...current, applicationKey: value }))}
          error={errors.applicationKey ?? null}
          required
        />
        {form.kind === 'customer-portal' ? (
          <TextField
            label="Portal ID (optional)"
            value={form.portalId}
            onChange={(value) => setForm((current) => ({ ...current, portalId: value }))}
            hint="The customer portal this host serves, when the organization has more than one."
            error={errors.portalId ?? null}
            spellCheck={false}
          />
        ) : null}
      </WriteDialog>
    );
  } else if (target?.kind === 'action') {
    const host = target.domain.hostname;
    const texts: Record<DomainAction, { title: string; submit: string; body: ReactNode; done: string }> = {
      verify: {
        title: `Check the DNS record of ${host} now?`,
        submit: 'Verify now',
        body: (
          <p>Trace looks for the TXT record. When it is there, the host is verified and can be enabled.</p>
        ),
        done: `Trace checked the DNS record of ${host}.`,
      },
      disable: {
        title: `Disable ${host}?`,
        submit: `Disable ${host}`,
        body: (
          <p className="admin-dialog-warning">
            People can no longer sign in through {host} until it is enabled again. Its registration and
            verification are kept.
          </p>
        ),
        done: `${host} is disabled.`,
      },
      enable: {
        title: `Enable ${host}?`,
        submit: `Enable ${host}`,
        body: (
          <p>
            {host} serves {name} again.
          </p>
        ),
        done: `${host} is enabled.`,
      },
      release: {
        title: `Release ${host}?`,
        submit: `Release ${host}`,
        body: (
          <>
            <p className="admin-dialog-warning">
              {host} stops serving {name} at once. Releasing cannot be undone.
            </p>
            <p>
              Trace then holds the host for a cooldown before any other organization can register it, and
              every new registration of it, {name}&apos;s included, needs fresh DNS verification.
            </p>
          </>
        ),
        done: `${host} is released and held for the cooldown.`,
      },
    };
    const text = texts[target.action];
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={text.title}
        submitLabel={text.submit}
        tone={target.action === 'release' || target.action === 'disable' ? 'danger' : 'default'}
        onSubmit={async () => {
          const problem = reasonError(reason);
          setReasonProblem(problem);
          if (problem) return 'invalid';
          await api.domainAction(organizationId, target.domain.id, target.action, reason);
          return undefined;
        }}
        onSaved={() => {
          setNotice(text.done);
          read.retry();
        }}
      >
        {text.body}
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    );
  }

  return (
    <>
      <SavedNotice message={notice} />
      <PageSection
        title="Custom domains"
        description="Hosts that serve this organization's employee apps and customer portal, released ones included."
        action={
          abilities.manageDomains ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => open({ kind: 'add' })}
            >
              Add a domain
            </button>
          ) : undefined
        }
      >
        {read.failure ? (
          <AdminProblem
            failure={read.failure}
            subject="the domains"
            onRetry={read.retry}
            supportAccess={hint}
          />
        ) : !domains ? (
          <LoadingBlock label="Loading the domains" />
        ) : domains.length === 0 ? (
          <EmptyPageState
            icon={<NavigationGlyph name="Building2" />}
            title="No custom domains"
            description="The organization uses the Trace addresses only."
          />
        ) : (
          <ScrollTable
            caption="Custom domains"
            busy={read.loading}
            className="admin-table admin-table--domains-admin"
          >
            <thead>
              <tr>
                <th scope="col">Host</th>
                <th scope="col">Kind</th>
                <th scope="col">Product</th>
                <th scope="col">Verification</th>
                <th scope="col">TLS</th>
                <th scope="col">Lifecycle</th>
                <th scope="col">Notes</th>
                {abilities.manageDomains ? <th scope="col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.id}>
                  <th scope="row">
                    <code>{domain.hostname}</code>
                  </th>
                  <td>{DOMAIN_KIND_LABELS[domain.kind] ?? domain.kind}</td>
                  <td>{productName(domain.applicationKey)}</td>
                  <td>
                    <StatusBadge status={domain.verificationStatus} />
                  </td>
                  <td>
                    <StatusBadge status={domain.tlsStatus} />
                  </td>
                  <td>
                    <StatusBadge status={domain.lifecycle} />
                  </td>
                  <td className="admin-cell-wrap">
                    <DomainNote domain={domain} />
                  </td>
                  {abilities.manageDomains ? (
                    <td>
                      {domainActions(domain).length === 0 ? (
                        <span className="admin-muted">None</span>
                      ) : (
                        <div className="admin-row-actions">
                          {domainActions(domain).map((action) => (
                            <button
                              key={action}
                              type="button"
                              className={`admin-action${action === 'release' ? ' admin-action--danger' : ''}`}
                              aria-label={`${ACTION_LABELS[action]} ${domain.hostname}`}
                              onClick={() => open({ kind: 'action', action, domain })}
                            >
                              {ACTION_LABELS[action]}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        )}
      </PageSection>
      {dialogView}
      <VerificationRecord created={created} onDone={() => setCreated(null)} />
    </>
  );
}
